import React, { useState, useEffect, useRef } from "react";
import "./articles.css";
import Favo from "../../media/fav.jpeg"; 
import { removeFavorite, createReview, getAIReviewThemeAndPresentation, isAiActivated, fetchFavorites, getAIReviewThemeAndPresentationByUser, getAIReviewThemeAndPresentationExpert, getAIReviewThemeAndPresentationByUserExpert, fetchExpertReviewPreferences } from "../../HTTP/articles" 
import * as helpers from "../../Helper/utils";
import userApi from "../../HTTP/user"
import { languages } from "../../global/countriesAndSources";
import { GenerateMultiFactChecking } from "./generateFactCheck";
import AddIcon from '@mui/icons-material/Add';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { fetchUserTokens } from "../../HTTP/subscription";
import { VISIBILITY_OPTIONS } from "../../Helper/visibilityOptions";

const CUSTOM_AUDIENCES_KEY = "zemus_expert_custom_audiences";
const CUSTOM_USES_KEY = "zemus_expert_custom_uses";

const AUDIENCE_OPTIONS = [
  { value: "grand_public", label: "Grand public" },
  { value: "etudiants", label: "Étudiants" },
  { value: "enseignants", label: "Enseignants" },
  { value: "chercheurs_universitaires", label: "Chercheurs et universitaires" },
  { value: "journalistes_medias", label: "Journalistes et médias" },
  { value: "professionnels_decideurs", label: "Professionnels et décideurs" },
  { value: "entreprises_dirigeants", label: "Entreprises et dirigeants" },
  { value: "administrations_institutions", label: "Administrations et institutions" },
  { value: "associations_ong", label: "Associations et ONG" },
  { value: "experts_consultants", label: "Experts et consultants" }
];

const USE_OPTIONS = [
  { value: "information_personnelle", label: "Information personnelle" },
  { value: "publication_zemus", label: "Publication sur Zemus" },
  { value: "revue_de_presse_partagee", label: "Revue de presse à partager" },
  { value: "note_briefing", label: "Note de briefing" },
  { value: "rapport_professionnel", label: "Rapport professionnel" },
  { value: "presentation_reunion", label: "Présentation ou réunion" },
  { value: "veille_strategique", label: "Veille stratégique" },
  { value: "recherche_universitaire", label: "Recherche universitaire" },
  { value: "support_pedagogique", label: "Support pédagogique" },
  { value: "aide_decision", label: "Aide à la décision" },
  { value: "communication_institutionnelle", label: "Communication institutionnelle" }
];

const LENGTH_OPTIONS = [
  { value: "short", label: "Courte - 300 à 500 mots", minWords: 300, maxWords: 500 },
  { value: "standard", label: "Standard - 800 à 1200 mots", minWords: 800, maxWords: 1200 },
  { value: "long", label: "Approfondie - 2000 à 3000 mots", minWords: 2000, maxWords: 3000 },
  { value: "very_long", label: "Très approfondie - 3000 à 5000 mots", minWords: 3000, maxWords: 5000 },
  { value: "custom", label: "Personnalisée" }
];

const PERIOD_OPTIONS = [
  { value: "selected_articles", label: "Date des articles sélectionnés" },
  { value: "last_7_days", label: "7 derniers jours" },
  { value: "last_30_days", label: "30 derniers jours" },
  { value: "last_3_months", label: "3 derniers mois" },
  { value: "last_12_months", label: "12 derniers mois" },
  { value: "custom", label: "Choisir une période" }
];

const SOURCES_OPTIONS = [
  { value: "none", label: "Aucune - articles sélectionnés uniquement" },
  { value: "web_search", label: "Recherche sur Internet" },
  { value: "official_sources", label: "Sources officielles et institutionnelles" },
  { value: "scientific_publications", label: "Publications scientifiques et universitaires" },
  { value: "all_relevant", label: "Toutes les sources pertinentes" }
];

// Date locale au format YYYY-MM-DD, décalée de offsetDays jours
function localIsoDate(offsetDays) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function loadCustomList(key) {
  try {
    const list = JSON.parse(localStorage.getItem(key) || "[]");
    return Array.isArray(list) ? list.filter(v => typeof v === "string" && v.trim()) : [];
  } catch (e) {
    return [];
  }
}

// Payload envoyé à POST /ai/title-and-presentation-review-expert.
// Les champs vides sont omis (tous facultatifs côté serveur).
function buildExpertPayload(p, customAudiences, customUses) {
  const payload = {};
  const theme = p.themeOrScope.trim();
  if (theme) payload.themeOrScope = theme;
  if (p.reviewCutoffDate) payload.reviewCutoffDate = p.reviewCutoffDate;

  const audience = customAudiences.includes(p.priorityAudience)
    ? { type: "custom", value: p.priorityAudience, label: p.priorityAudience }
    : { type: "preset", value: p.priorityAudience, label: AUDIENCE_OPTIONS.find(o => o.value === p.priorityAudience)?.label };
  if (p.priorityAudience) payload.priorityAudience = audience;

  const use = customUses.includes(p.intendedUse)
    ? { type: "custom", value: p.intendedUse, label: p.intendedUse }
    : { type: "preset", value: p.intendedUse, label: USE_OPTIONS.find(o => o.value === p.intendedUse)?.label };
  if (p.intendedUse) payload.intendedUse = use;

  if (p.lengthPreset === "custom") {
    const words = parseInt(p.customWordCount, 10);
    if (words > 0) payload.length = { preset: "custom", targetWords: words };
  } else {
    const l = LENGTH_OPTIONS.find(o => o.value === p.lengthPreset);
    if (l) payload.length = { preset: l.value, minWords: l.minWords, maxWords: l.maxWords };
  }

  payload.period = p.periodPreset === "custom"
    ? { preset: "custom", startDate: p.periodStart || null, endDate: p.periodEnd || null }
    : { preset: p.periodPreset };

  const area = p.geographicArea.trim();
  if (area) payload.geographicArea = area;
  payload.supplementarySources = p.supplementarySources || "none";
  return payload;
}

const ArticlesFav = () => {
  // États principaux
  const [user, setUser] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // États de l'interface
  const [showPopup, setShowPopup] = useState(false);
  const [showAIOptions, setShowAIOptions] = useState(false);
  const [showAIUserOptions, setShowAIUserOptions] = useState(false);
  const [showAIExpertOptions, setShowAIExpertOptions] = useState(false);
  const expertPrefsLoaded = useRef(false);
  const [expertParams, setExpertParams] = useState(() => ({
    themeOrScope: "",
    reviewCutoffDate: localIsoDate(0),
    priorityAudience: "professionnels_decideurs",
    intendedUse: "note_briefing",
    lengthPreset: "standard",
    customWordCount: 1000,
    periodPreset: "selected_articles",
    periodStart: localIsoDate(-14),
    periodEnd: localIsoDate(0),
    geographicArea: "",
    supplementarySources: "none"
  }));
  const [customAudiences, setCustomAudiences] = useState(() => loadCustomList(CUSTOM_AUDIENCES_KEY));
  const [customUses, setCustomUses] = useState(() => loadCustomList(CUSTOM_USES_KEY));
  const [newAudience, setNewAudience] = useState("");
  const [newUse, setNewUse] = useState("");
  const [showAIUserExpertOptions, setShowAIUserExpertOptions] = useState(false);
  const [userExpertParams, setUserExpertParams] = useState({
    centralQuestion: "",
    priorityAudience: "",
    geographicInstitutionalScope: "",
    purpose: "",
    timeHorizon: "",
    desiredLength: "",
    supplementarySources: "provided-only"
  });
  const [userPrompt, setUserPrompt] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState(sessionStorage.getItem('currentlanguage') || "fr");
  const [selectedTone, setSelectedTone] = useState("synthétique");
  const [selection, setSelection] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [reviewTheme, setReviewTheme] = useState("");
  const [reviewPresentation, setReviewPresentation] = useState("");
  const [visible, setVisible] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [aiGenerated, setAiGenerated] = useState(false);
  const [aiActivated, setAiActivated] = useState(false);
  const [showFactCheckingPopup, setShowFactCheckingPopup] = useState(false);
  const [expandedReviews, setExpandedReviews] = useState({});
  const [userTokens, setUserTokens] = useState(1);
  const listRef = useRef(null);
  
  const toneOptions = [
    "synthétique",
    "journalistique", 
    "pédagogique",
    "académique",
    "diplomatique",
    "narratif",
    "littéraire",
    "humoristique", 
    "polémique",
    "engagé/militant"
  ];

  // Récupération des informations utilisateur
  useEffect(() => {
    const userData = localStorage.getItem("user");
    
    if (!userData) {
      window.location.href = '/';
      return;
    }
    
    try {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
    } catch (error) {
      console.error('Erreur lors du parsing des données utilisateur:', error);
      window.location.href = '/';
    }
  }, []);

  // Chargement des données depuis l'API
  useEffect(() => {
    if (!user?.id) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Charger les favoris, reviews et statut AI en parallèle
        const [userTokens, favoritesData, reviewsData, aiStatus] = await Promise.allSettled([
          fetchUserTokens(user.id),
          fetchFavorites(),
          userApi.getUserReviews(user.id),
          isAiActivated()
        ]);

        console.log(userTokens, favoritesData, reviewsData, aiStatus);
        // Traitement des tokens utilisateur
        if (userTokens.status === 'fulfilled') {
          setUserTokens(userTokens.value.tokens);
        } else {
          console.error('Erreur lors du chargement des tokens utilisateur:', userTokens.reason);
          setUserTokens(0); // Valeur par défaut en cas d'erreur
        }

        // Traitement des favoris
        if (favoritesData.status === 'fulfilled') {
          setFavorites(favoritesData.value || []);
        } else {
          console.error('Erreur lors du chargement des favoris:', favoritesData.reason);
        }

        // Traitement des reviews
        if (reviewsData.status === 'fulfilled') {
          setReviews(reviewsData.value?.reviews || []);
        } else {
          console.error('Erreur lors du chargement des reviews:', reviewsData.reason);
        }

        // Traitement du statut AI
        if (aiStatus.status === 'fulfilled' && aiStatus.value !== undefined) {
          setAiActivated(aiStatus.value.value === "1");
        } else {
          console.error('Erreur lors du chargement du statut AI:', aiStatus.reason);
        }

      } catch (error) {
        console.error('Erreur générale lors du chargement des données:', error);
        setError('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.id]);

  // Gestion de la sélection
  const handleSelectionChange = (favoriteId, isSelected) => {
    setSelection(prev => {
      if (isSelected) {
        const favorite = favorites.find(fav => fav.id === favoriteId);
        return favorite ? [...prev, favorite] : prev;
      } else {
        return prev.filter(fav => fav.id !== favoriteId);
      }
    });
  };

  const handleSelectAll = () => {
    if (selection.length === filteredFavorites.length) {
      // Tout désélectionner
      setSelection([]);
    } else {
      // Tout sélectionner
      setSelection([...filteredFavorites]);
    }
  };

  // Suppression de favoris
  const handleRemoveMultipleFavorites = async () => {
    if (selection.length === 0) {
      alert("Veuillez sélectionner au moins un article avant de supprimer.");
      return;
    }

    setLoading(true);
    const errors = [];

    try {
      // Supprimer chaque favori sélectionné
      await Promise.allSettled(
        selection.map(async (favorite) => {
          try {
            await removeFavorite(favorite);
            return favorite.id;
          } catch (error) {
            errors.push(`Erreur lors de la suppression de "${favorite.title}"`);
            throw error;
          }
        })
      );

      // Mettre à jour la liste des favoris
      const removedIds = selection.map(fav => fav.id);
      setFavorites(prev => prev.filter(fav => !removedIds.includes(fav.id)));
      setSelection([]);
      
      if (errors.length === 0) {
        alert('Favoris supprimés avec succès');
      } else {
        alert(`Suppression terminée avec ${errors.length} erreur(s)`);
      }

    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression des favoris');
    } finally {
      setLoading(false);
    }
  };

  // Filtrage des favoris
  const filteredFavorites = favorites.filter(fav => 
    fav.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    fav.link?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    fav.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    fav.country?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Gestion des popups
  const togglePopup = () => {
    setShowPopup(!showPopup);
    setAiGenerated(false);
    setShowAIOptions(false);
    setShowAIUserOptions(false);
    setShowAIExpertOptions(false);
    setShowAIUserExpertOptions(false);
  };

  const toggleAIOptions = () => {
    if (showAIUserOptions) setShowAIUserOptions(false);
    setShowAIOptions(!showAIOptions);
  };

  const toggleAIUserOptions = () => {
    if (showAIOptions) setShowAIOptions(false);
    setShowAIUserOptions(!showAIUserOptions);
  };

  const toggleAIExpertOptions = () => {
    const opening = !showAIExpertOptions;
    setShowAIExpertOptions(opening);
    if (opening && !expertPrefsLoaded.current) {
      expertPrefsLoaded.current = true;
      fetchExpertReviewPreferences().then(applyExpertPreferences);
    }
  };

  // Pré-remplit le formulaire avec les derniers choix de l'utilisateur (même forme que le payload envoyé)
  const applyExpertPreferences = (prefs) => {
    if (!prefs) return;
    const next = {};
    if (typeof prefs.themeOrScope === "string") next.themeOrScope = prefs.themeOrScope;
    if (typeof prefs.geographicArea === "string") next.geographicArea = prefs.geographicArea;
    if (prefs.supplementarySources) next.supplementarySources = prefs.supplementarySources;

    const readChoice = (choice, list, setList, key, builtins) => {
      if (!choice) return undefined;
      const value = typeof choice === "string" ? choice : choice.value;
      if (!value) return undefined;
      const isBuiltin = builtins.some(o => o.value === value);
      if (!isBuiltin) {
        const merged = list.includes(value) ? list : [...list, value];
        if (merged !== list) {
          setList(merged);
          try { localStorage.setItem(key, JSON.stringify(merged)); } catch (e) { /* stockage indisponible */ }
        }
      }
      return value;
    };
    const audience = readChoice(prefs.priorityAudience, customAudiences, setCustomAudiences, CUSTOM_AUDIENCES_KEY, AUDIENCE_OPTIONS);
    if (audience) next.priorityAudience = audience;
    const use = readChoice(prefs.intendedUse, customUses, setCustomUses, CUSTOM_USES_KEY, USE_OPTIONS);
    if (use) next.intendedUse = use;

    const length = prefs.length;
    if (length?.preset && LENGTH_OPTIONS.some(o => o.value === length.preset)) {
      next.lengthPreset = length.preset;
      if (length.preset === "custom" && length.targetWords) next.customWordCount = length.targetWords;
    }
    const period = prefs.period;
    if (period?.preset && PERIOD_OPTIONS.some(o => o.value === period.preset)) {
      next.periodPreset = period.preset;
      if (period.preset === "custom") {
        if (period.startDate) next.periodStart = period.startDate;
        if (period.endDate) next.periodEnd = period.endDate;
      }
    }
    // La date de référence reste celle d'aujourd'hui
    setExpertParams(prev => ({ ...prev, ...next }));
  };

  const updateExpertParam = (name, value) => {
    setExpertParams(prev => ({ ...prev, [name]: value }));
  };

  const addCustomOption = (value, list, setList, key, setInput, paramName) => {
    const v = value.trim();
    if (!v) return;
    if (!list.includes(v)) {
      const next = [...list, v];
      setList(next);
      try { localStorage.setItem(key, JSON.stringify(next)); } catch (e) { /* stockage indisponible */ }
    }
    updateExpertParam(paramName, v);
    setInput("");
  };

  const toggleAIUserExpertOptions = () => {
    setShowAIUserExpertOptions(!showAIUserExpertOptions);
  };

  const updateUserExpertParam = (name, value) => {
    setUserExpertParams(prev => ({ ...prev, [name]: value }));
  };

  const toggleReviewsExpand = (index) => {
    setExpandedReviews(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Création de review
  const handleValidate = () => {
    if (document.getElementById('fact_check')?.checked) {
      if (selection.length === 0) {
        alert("Veuillez sélectionner au moins un article avant de générer un fact-checking.");
        return;
      }
      setShowFactCheckingPopup(true);
    } else {
      addReview();
    }
  };

  const addReview = async () => {
    const theme = document.getElementById('review_theme').value;
    const presentation = document.getElementById('review_presentation').value;
    
    try {
      const res = await createReview(
        theme, 
        presentation, 
        selection, 
        !aiGenerated ? document.getElementById('publishable_review')?.checked : 0,
        visible
      );
      
      setShowPopup(false);
      setSelection([]);
      setReviewTheme("");
      setReviewPresentation("");
      setAiGenerated(false);
      alert('Review créée avec succès');
      helpers.goToStaticPage(false, res.newReview);
      // window.location.href = `https://static.zemus.info/?id=${res.newReview}&redirect=favourites&readerid=${user.id}`;
    } catch (error) {
      console.error('Erreur lors de la création de la review:', error);
      alert('Erreur lors de la création de la review');
    }
  };

  // Génération avec IA
  const generateWithAI = async () => {
    if (selection.length === 0) {
      alert("Veuillez sélectionner au moins un article avant de générer du contenu.");
      return;
    }

    setIsLoading(true);
    
    try {
      const articleLinks = selection.map(item => helpers.htmlDecode(item.link));
      
      const result = await getAIReviewThemeAndPresentation(
        selectedLanguage,
        selectedTone,
        articleLinks,
        user.id, 
      );
      
      if (result?.theme) {
        document.getElementById('review_theme').value = result.theme;
        setReviewTheme(result.theme);
      }
      
      if (result?.presentation) {
        const formattedPresentation = result.presentation.map((item, index) => {
          return `${index + 1} -\n\n${item}`;
        });
        const presentationValue = formattedPresentation.join('\n\n');
        document.getElementById('review_presentation').value = presentationValue;
        setReviewPresentation(presentationValue);
      }
      
      setShowAIOptions(false);
      setAiGenerated(true);
      
    } catch (error) {
      console.error("Erreur lors de la génération avec l'IA:", error);
      alert("Une erreur s'est produite lors de la génération avec l'IA.");
    } finally {
      setIsLoading(false);
    }
  };

  const generateWithAIUser = async () => {
    if (selection.length === 0) {
      alert("Veuillez sélectionner au moins un article avant de générer du contenu.");
      return;
    }
    if (!userPrompt || userPrompt.trim().length === 0) {
      alert("Veuillez saisir un prompt avant de générer.");
      return;
    }

    setIsLoading(true);
    try {
      const articleLinks = selection.map(item => helpers.htmlDecode(item.link));
      const result = await getAIReviewThemeAndPresentationByUser(
        selectedLanguage,
        userPrompt.trim(),
        articleLinks,
        user.id
      );

      if (result?.theme) {
        const themeEl = document.getElementById('review_theme');
        if (themeEl) themeEl.value = result.theme;
        setReviewTheme(result.theme);
      }
      if (result?.presentation) {
        const presentationEl = document.getElementById('review_presentation');
        if (presentationEl) presentationEl.value = result.presentation;
        setReviewPresentation(result.presentation);
      }

      setShowAIUserOptions(false);
      setAiGenerated(true);
    } catch (error) {
      console.error("Erreur lors de la génération avec l'IA (prompt utilisateur):", error);
      alert("Une erreur s'est produite lors de la génération avec l'IA.");
    } finally {
      setIsLoading(false);
    }
  };

  const generateWithAIExpert = async () => {
    if (selection.length === 0) {
      alert("Veuillez sélectionner au moins un article avant de générer du contenu.");
      return;
    }

    setIsLoading(true);
    try {
      const articleLinks = selection.map(item => helpers.htmlDecode(item.link));
      const result = await getAIReviewThemeAndPresentationExpert(user.id, articleLinks, buildExpertPayload(expertParams, customAudiences, customUses));

      if (!result?.theme && !result?.presentation) {
        alert("Le service IA n'a renvoyé aucun contenu.");
        return;
      }

      const themeEl = document.getElementById('review_theme');
      if (themeEl) themeEl.value = result.theme || "";
      setReviewTheme(result.theme || "");

      const presentationEl = document.getElementById('review_presentation');
      if (presentationEl) presentationEl.value = result.presentation || "";
      setReviewPresentation(result.presentation || "");

      setShowAIExpertOptions(false);
      setAiGenerated(true);
    } catch (error) {
      console.error("Erreur lors de la génération de la revue de presse experte:", error);
      if (error?.status === 400) {
        alert(`Requête invalide : ${error.message}`);
      } else if (error?.status === 401) {
        // redirection vers la connexion déjà déclenchée par apiFetch
      } else {
        alert(error?.message || "Une erreur s'est produite lors de la génération avec l'IA.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const generateWithAIUserExpert = async () => {
    if (selection.length === 0) {
      alert("Veuillez sélectionner au moins un article avant de générer du contenu.");
      return;
    }

    setIsLoading(true);
    try {
      const articleLinks = selection.map(item => helpers.htmlDecode(item.link));
      const result = await getAIReviewThemeAndPresentationByUserExpert(user.id, articleLinks, userExpertParams);

      if (!result?.theme && !result?.presentation) {
        alert("Le service IA n'a renvoyé aucun contenu.");
        return;
      }

      // Valeurs affichées telles quelles dans des champs texte : aucun rendu Markdown
      const themeEl = document.getElementById('review_theme');
      if (themeEl) themeEl.value = result.theme || "";
      setReviewTheme(result.theme || "");

      const presentationEl = document.getElementById('review_presentation');
      if (presentationEl) presentationEl.value = result.presentation || "";
      setReviewPresentation(result.presentation || "");

      setShowAIUserExpertOptions(false);
      setAiGenerated(true);
    } catch (error) {
      console.error("Erreur lors de la génération de l'analyse stratégique experte:", error);
      if (error?.status === 400) {
        alert(`Requête invalide : ${error.message}`);
      } else if (error?.status !== 401) {
        alert(error?.message || "Une erreur s'est produite lors de la génération avec l'IA.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Navigation vers d'autres pages
  const addToExistingReview = () => {
    const jsonString = JSON.stringify(selection);
    const encodedString = encodeURIComponent(jsonString);
    window.location.href = `/addtoreview?data=${encodedString}`;
  };

  const addToExistingFriendReview = () => { 
    const jsonString = JSON.stringify(selection);
    const encodedString = encodeURIComponent(jsonString);
    window.location.href = `/addtofriendreview?data=${encodedString}`;
  };

  // Fonction pour remonter en haut
  const scrollToTop = () => {
    if (listRef.current) {
      listRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };


  // Rendu des reviews
  const renderReviews = (faviLink, index) => {
    const faviReviews = reviews.filter(review => 
      review.articles && review.articles.some(article => 
        helpers.htmlDecode(article.link) === helpers.htmlDecode(faviLink)
      )
    );
    
    if (faviReviews.length === 0) return null;
    
    const isExpanded = expandedReviews[index] || false;
    const displayedReviews = isExpanded ? faviReviews : faviReviews.slice(0, 2);
    
    return (
      <div className="review mb-4">
        <h4 className="text-black text-xs lg:text-base mb-1">
          Présent dans {faviReviews.length > 1 ? "ces" : "cette"} revue{faviReviews.length > 1 ? "s" : ""} de presse :
        </h4>
        <ul>
          {displayedReviews.map((review, idx) => (
            <li key={idx} className="text-black text-xs lg:text-base">
              <button onClick={() => helpers.goToStaticPage(false, review.id)} className="text-cyan-600 hover:underline">
                - {helpers.htmlDecode(review.theme)}
              </button>
            </li>
          ))}
        </ul>
        
        {faviReviews.length > 2 && (
          <button 
            onClick={() => toggleReviewsExpand(index)}
            className="text-cyan-600 text-sm mt-1 flex items-center hover:text-cyan-800"
          >
            {isExpanded ? "Voir moins" : `Voir ${faviReviews.length - 2} de plus`}
            <ExpandMoreIcon 
              fontSize="small" 
              className={`ml-1 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            />
          </button>
        )}
      </div>
    );
  };

  // Affichage du chargement
  if (loading) {
    return (
      <section className="articlesfav">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#009AAD]"></div>
          <p className="ml-4 text-[#009AAD]">Chargement des favoris...</p>
        </div>
      </section>
    );
  }

  // Affichage d'erreur
  if (error) {
    return (
      <section className="articlesfav">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-red-500 mb-4">{error}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="bg-[#009AAD] text-white px-4 py-2 rounded"
            >
              Réessayer
            </button>
          </div>
        </div>
      </section>
    );
  }

  const isOneSelected = selection.length > 0;
  const canValidate =
    reviewTheme.trim().length > 0 && reviewPresentation.trim().length > 0;

  return (
    <>
      {showPopup && <div className="blur-background"></div>}
      {showPopup && (
        <div className="popup"> 
          <div style={{ display: "flex" }} className="headtitle">
            <h2>Créer une revue de Presse</h2>
            <span onClick={togglePopup} className="material-symbols-outlined">
              close
            </span>
          </div>
          <div>
            <div className="Message">
              <input
                title="Write Message"
                name="theme"
                placeholder="Titre..."
                className="MsgInput"
                type="text"
                maxLength="255"
                id="review_theme"
                value={reviewTheme}
                onChange={(e) => setReviewTheme(e.target.value)}
              /> 
            </div>
            <div className="Message">
              <textarea
                name="presentation"
                placeholder="Description..."
                className="MsgInput"
                rows={aiGenerated && reviewPresentation.length > 1500 ? 16 : 4}
                id="review_presentation"
                value={reviewPresentation}
                onChange={(e) => setReviewPresentation(e.target.value)}
              /> 
            </div>
            
            {!aiGenerated && (
              <div className="Message">
                <input
                  name="publishable_review"
                  type="checkbox"
                  maxLength="255"
                  id="publishable_review"
                /> 
                <label htmlFor="publishable_review" className="text-white ml-2">
                  Autoriser mes amis à publier leurs propres favoris dans cette revue de presse
                </label>
              </div>
            )}

            {aiGenerated && (
              <div className="Message">
                <input
                  name="fact_check"
                  type="checkbox"
                  id="fact_check"
                /> 
                <label htmlFor="fact_check" className="text-white ml-2">
                  Générer un fact-checking pour cette revue de presse
                </label>
              </div>
            )}
            
            <div className="Message">
              {aiActivated && !aiGenerated && userTokens > 0 &&               !showAIUserOptions && !showAIExpertOptions && !showAIUserExpertOptions && (
                <button 
                  className="border-2 border-solid border-black bg-button text-black font-bold py-2 px-5 rounded mb-2 mr-2"
                  onClick={toggleAIOptions}
                  disabled={isLoading}
                >
                  {showAIOptions ? "Retour" : "Synthèse"}
                </button>
              )}

              {aiActivated && !aiGenerated && userTokens > 0 &&               !showAIOptions && !showAIExpertOptions && !showAIUserExpertOptions && (
              <button 
                  className="border-2 border-solid border-black bg-button text-black font-bold py-2 px-5 rounded mb-2 mr-2"
                  onClick={toggleAIUserOptions}
                  disabled={isLoading}
                >
                  {showAIUserOptions ? "Retour" : "Analyse"}
                </button>
              )}

              {aiActivated && !aiGenerated && userTokens > 0 &&               !showAIOptions && !showAIUserOptions && !showAIUserExpertOptions && (
                <button 
                  className="border-2 border-solid border-black bg-button text-black font-bold py-2 px-5 rounded mb-2 mr-2"
                  onClick={toggleAIExpertOptions}
                  disabled={isLoading}
                >
                  {showAIExpertOptions ? "Retour" : "Synthèse experte"}
                </button>
              )}

                            {aiActivated && !aiGenerated && userTokens > 0 && !showAIOptions && !showAIUserOptions && !showAIExpertOptions && (
                              <button 
                                className="border-2 border-solid border-black bg-button text-black font-bold py-2 px-5 rounded mb-2 mr-2"
                                onClick={toggleAIUserExpertOptions}
                                disabled={isLoading}
                              >
                                {showAIUserExpertOptions ? "Retour" : "Analyse experte"}
                              </button>
                            )}
              
              
            </div>
            
            {aiActivated && showAIOptions && (
              <div className="ai-options p-3 border border-gray-300 rounded mt-3 mb-3">
                <h3 className="text-white font-bold text-lg mb-3">Résumé</h3>
                <div className="flex flex-col sm:flex-row gap-3 mb-3">
                  <div className="flex-1">
                    <label htmlFor="language-select"  className="block text-white mb-1">
                      Langue:
                    </label>
                    <select
                      id="language-select"
                      className="w-full p-2 rounded"
                      value={selectedLanguage}
                      onChange={(e) => setSelectedLanguage(e.target.value)}
                      disabled={isLoading}
                    >
                      {languages.map((lang) => (
                        <option key={lang.language_code} value={lang.language_code}>
                          {lang.language_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="flex-1">
                    <label htmlFor="tone-select" className="block text-white mb-1">
                      Ton journalistique:
                    </label>
                    <select
                      id="tone-select"
                      className="w-full p-2 rounded"
                      value={selectedTone}
                      onChange={(e) => setSelectedTone(e.target.value)}
                      disabled={isLoading}
                    >
                      {toneOptions.map((tone) => (
                        <option key={tone} value={tone}>
                          {tone.charAt(0).toUpperCase() + tone.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <button
                  className={`text-white font-bold py-2 px-5 rounded w-full flex justify-center items-center ${
                    isLoading ? 'bg-gray-500' : 'bg-green-500 hover:bg-green-700'
                  }`}
                  onClick={generateWithAI}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin h-5 w-5 mr-2 border-t-2 border-b-2 border-white rounded-full"></div>
                      Génération en cours...
                    </div>
                  ) : (
                    'Générer'
                  )}
                </button>
              </div>
            )}

            { aiActivated && showAIUserOptions && (
              <div className="ai-options p-3 border border-gray-300 rounded mt-3 mb-3">
                <h3 className="text-white font-bold text-lg mb-3">Analyse</h3>
                <div className="flex flex-col sm:flex-row gap-3 mb-3">
                  <div className="flex-1">
                    <label htmlFor="language-select-user"  className="block text-white mb-1">
                      Langue :
                    </label>
                    <select
                      id="language-select-user"
                      className="w-full p-2 rounded"
                      value={selectedLanguage}
                      onChange={(e) => setSelectedLanguage(e.target.value)}
                      disabled={isLoading}
                    >
                      {languages.map((lang) => (
                        <option key={lang.language_code} value={lang.language_code}>
                          {lang.language_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mb-3">
                  <label htmlFor="user-prompt" className="block text-white mb-1">
                    Prompt :
                  </label>
                  <textarea
                    id="user-prompt"
                    className="w-full p-2 rounded"
                    rows={5}
                    placeholder="Décrivez précisément le type de résumé attendu (angle, structure, style, public cible, longueur, etc.)"
                    value={userPrompt}
                    onChange={(e) => setUserPrompt(e.target.value)}
                    disabled={isLoading}
                  />
                </div>

                <button
                  className={`text-white font-bold py-2 px-5 rounded w-full flex justify-center items-center ${isLoading ? 'bg-gray-500' : 'bg-green-500 hover:bg-green-700'}`}
                  onClick={generateWithAIUser}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin h-5 w-5 mr-2 border-t-2 border-b-2 border-white rounded-full"></div>
                      Génération en cours...
                    </div>
                  ) : (
                    'Générer'
                  )}
                </button>
              </div>
            )}


            { aiActivated && showAIExpertOptions && (
              <div className="ai-options p-3 border border-gray-300 rounded mt-3 mb-3">
                <h3 className="text-white font-bold text-lg mb-3">Revue de presse experte</h3>
                <p className="text-white text-sm mb-3">
                  Tous les champs sont facultatifs. Les articles sélectionnés servent de base à la revue.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 mb-3">
                  <div className="flex-1">
                    <label htmlFor="expert-theme" className="block text-white mb-1">Sujet (thème ou périmètre) :</label>
                    <input id="expert-theme" type="text" className="w-full p-2 rounded" value={expertParams.themeOrScope}
                      placeholder="Ex. : Relations internationales, transition énergétique, éducation..."
                      onChange={(e) => updateExpertParam('themeOrScope', e.target.value)} disabled={isLoading} />
                  </div>
                  <div className="flex-1">
                    <label htmlFor="expert-period" className="block text-white mb-1">Période des articles :</label>
                    <select id="expert-period" className="w-full p-2 rounded" value={expertParams.periodPreset}
                      onChange={(e) => updateExpertParam('periodPreset', e.target.value)} disabled={isLoading}>
                      {PERIOD_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    {expertParams.periodPreset === "custom" && (
                      <div className="flex gap-2 mt-2">
                        <input type="date" aria-label="Date de début" className="w-full p-2 rounded" value={expertParams.periodStart}
                          max={expertParams.periodEnd || undefined}
                          onChange={(e) => updateExpertParam('periodStart', e.target.value)} disabled={isLoading} />
                        <input type="date" aria-label="Date de fin" className="w-full p-2 rounded" value={expertParams.periodEnd}
                          min={expertParams.periodStart || undefined}
                          onChange={(e) => updateExpertParam('periodEnd', e.target.value)} disabled={isLoading} />
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 mb-3">
                  <div className="flex-1">
                    <label htmlFor="expert-cutoff" className="block text-white mb-1">Date de référence (date d'arrêt) :</label>
                    <input id="expert-cutoff" type="date" className="w-full p-2 rounded" value={expertParams.reviewCutoffDate}
                      onChange={(e) => updateExpertParam('reviewCutoffDate', e.target.value)} disabled={isLoading} />
                  </div>
                  <div className="flex-1">
                    <label htmlFor="expert-area" className="block text-white mb-1">Zone géographique :</label>
                    <input id="expert-area" type="text" className="w-full p-2 rounded" value={expertParams.geographicArea}
                      placeholder="Ex. : France, Europe, Afrique, Moyen-Orient..."
                      onChange={(e) => updateExpertParam('geographicArea', e.target.value)} disabled={isLoading} />
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 mb-3">
                  <div className="flex-1">
                    <label htmlFor="expert-audience" className="block text-white mb-1">Public cible :</label>
                    <select id="expert-audience" className="w-full p-2 rounded" value={expertParams.priorityAudience}
                      onChange={(e) => updateExpertParam('priorityAudience', e.target.value)} disabled={isLoading}>
                      {AUDIENCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      {customAudiences.map(a => <option key={`c-${a}`} value={a}>{a}</option>)}
                    </select>
                    <div className="flex gap-2 mt-2">
                      <input type="text" aria-label="Public personnalisé" className="w-full p-2 rounded" value={newAudience}
                        placeholder="Ajouter un public personnalisé (ex. : Journalistes, ONG...)"
                        onChange={(e) => setNewAudience(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomOption(newAudience, customAudiences, setCustomAudiences, CUSTOM_AUDIENCES_KEY, setNewAudience, 'priorityAudience'); } }}
                        disabled={isLoading} />
                      <button type="button" className="bg-blue-500 hover:bg-blue-700 text-white font-bold px-3 rounded"
                        onClick={() => addCustomOption(newAudience, customAudiences, setCustomAudiences, CUSTOM_AUDIENCES_KEY, setNewAudience, 'priorityAudience')}
                        disabled={isLoading}>+ Ajouter</button>
                    </div>
                  </div>
                  <div className="flex-1">
                    <label htmlFor="expert-use" className="block text-white mb-1">Utilisation prévue :</label>
                    <select id="expert-use" className="w-full p-2 rounded" value={expertParams.intendedUse}
                      onChange={(e) => updateExpertParam('intendedUse', e.target.value)} disabled={isLoading}>
                      {USE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      {customUses.map(u => <option key={`c-${u}`} value={u}>{u}</option>)}
                    </select>
                    <div className="flex gap-2 mt-2">
                      <input type="text" aria-label="Utilisation personnalisée" className="w-full p-2 rounded" value={newUse}
                        placeholder="Ajouter une utilisation personnalisée (ex. : Cours, Rapport interne...)"
                        onChange={(e) => setNewUse(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomOption(newUse, customUses, setCustomUses, CUSTOM_USES_KEY, setNewUse, 'intendedUse'); } }}
                        disabled={isLoading} />
                      <button type="button" className="bg-blue-500 hover:bg-blue-700 text-white font-bold px-3 rounded"
                        onClick={() => addCustomOption(newUse, customUses, setCustomUses, CUSTOM_USES_KEY, setNewUse, 'intendedUse')}
                        disabled={isLoading}>+ Ajouter</button>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 mb-3">
                  <div className="flex-1">
                    <label htmlFor="expert-length" className="block text-white mb-1">Longueur du contenu :</label>
                    <select id="expert-length" className="w-full p-2 rounded" value={expertParams.lengthPreset}
                      onChange={(e) => updateExpertParam('lengthPreset', e.target.value)} disabled={isLoading}>
                      {LENGTH_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    {expertParams.lengthPreset === "custom" && (
                      <input type="number" min="1" step="50" aria-label="Nombre de mots" className="w-full p-2 rounded mt-2"
                        value={expertParams.customWordCount} placeholder="Nombre de mots"
                        onChange={(e) => updateExpertParam('customWordCount', e.target.value)} disabled={isLoading} />
                    )}
                  </div>
                  <div className="flex-1">
                    <label htmlFor="expert-sources" className="block text-white mb-1">Sources complémentaires :</label>
                    <select id="expert-sources" className="w-full p-2 rounded" value={expertParams.supplementarySources}
                      onChange={(e) => updateExpertParam('supplementarySources', e.target.value)} disabled={isLoading}>
                      {SOURCES_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                </div>

                <button
                  className={`text-white font-bold py-2 px-5 rounded w-full flex justify-center items-center ${isLoading ? 'bg-gray-500' : 'bg-green-500 hover:bg-green-700'}`}
                  onClick={generateWithAIExpert}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin h-5 w-5 mr-2 border-t-2 border-b-2 border-white rounded-full"></div>
                      Génération en cours...
                    </div>
                  ) : (
                    'Générer'
                  )}
                </button>
              </div>
            )}

            { aiActivated && showAIUserExpertOptions && (
              <div className="ai-options p-3 border border-gray-300 rounded mt-3 mb-3">
                <h3 className="text-white font-bold text-lg mb-3">Analyse stratégique experte</h3>
                <p className="text-white text-sm mb-3">
                  Tous les champs sont facultatifs. Les articles sélectionnés servent de base à la note.
                </p>
                <div className="mb-3">
                  <label htmlFor="uexpert-question" className="block text-white mb-1">Question centrale :</label>
                  <textarea id="uexpert-question" className="w-full p-2 rounded" rows={3} value={userExpertParams.centralQuestion}
                    onChange={(e) => updateUserExpertParam('centralQuestion', e.target.value)} disabled={isLoading} />
                </div>
                <div className="flex flex-col sm:flex-row gap-3 mb-3">
                  <div className="flex-1">
                    <label htmlFor="uexpert-audience" className="block text-white mb-1">Public prioritaire :</label>
                    <input id="uexpert-audience" type="text" className="w-full p-2 rounded" value={userExpertParams.priorityAudience}
                      onChange={(e) => updateUserExpertParam('priorityAudience', e.target.value)} disabled={isLoading} />
                  </div>
                  <div className="flex-1">
                    <label htmlFor="uexpert-scope" className="block text-white mb-1">Périmètre géographique et institutionnel :</label>
                    <input id="uexpert-scope" type="text" className="w-full p-2 rounded" value={userExpertParams.geographicInstitutionalScope}
                      onChange={(e) => updateUserExpertParam('geographicInstitutionalScope', e.target.value)} disabled={isLoading} />
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 mb-3">
                  <div className="flex-1">
                    <label htmlFor="uexpert-purpose" className="block text-white mb-1">Finalité :</label>
                    <input id="uexpert-purpose" type="text" className="w-full p-2 rounded" value={userExpertParams.purpose}
                      onChange={(e) => updateUserExpertParam('purpose', e.target.value)} disabled={isLoading} />
                  </div>
                  <div className="flex-1">
                    <label htmlFor="uexpert-horizon" className="block text-white mb-1">Horizon temporel :</label>
                    <input id="uexpert-horizon" type="text" className="w-full p-2 rounded" value={userExpertParams.timeHorizon}
                      onChange={(e) => updateUserExpertParam('timeHorizon', e.target.value)} disabled={isLoading} />
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 mb-3">
                  <div className="flex-1">
                    <label htmlFor="uexpert-length" className="block text-white mb-1">Longueur souhaitée :</label>
                    <input id="uexpert-length" type="text" className="w-full p-2 rounded" value={userExpertParams.desiredLength}
                      onChange={(e) => updateUserExpertParam('desiredLength', e.target.value)} disabled={isLoading} />
                  </div>
                  <div className="flex-1">
                    <label htmlFor="uexpert-sources" className="block text-white mb-1">Sources complémentaires :</label>
                    <select id="uexpert-sources" className="w-full p-2 rounded" value={userExpertParams.supplementarySources}
                      onChange={(e) => updateUserExpertParam('supplementarySources', e.target.value)} disabled={isLoading}>
                      <option value="provided-only">Sources fournies uniquement</option>
                      <option value="verification">Vérification</option>
                    </select>
                  </div>
                </div>

                <button
                  className={`text-white font-bold py-2 px-5 rounded w-full flex justify-center items-center ${isLoading ? 'bg-gray-500' : 'bg-green-500 hover:bg-green-700'}`}
                  onClick={generateWithAIUserExpert}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin h-5 w-5 mr-2 border-t-2 border-b-2 border-white rounded-full"></div>
                      Génération en cours...
                    </div>
                  ) : (
                    'Générer'
                  )}
                </button>
              </div>
            )}

            {isLoading && (
              <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                <div className="bg-white p-5 rounded-lg shadow-lg flex flex-col items-center">
                  <div className="w-12 h-12 border-4 border-t-4 ... animate-spin mb-3" style={{ borderTopColor: '#3B82F6' }}></div>
                  <p className="text-gray-700 font-medium">Génération en cours...</p>
                </div>
              </div>
            )}
            
            <div className="Message">
              {selection.map((fav, i) => (
                <img
                  key={i}
                  className="item-photofav-add"
                  src={helpers.htmlDecode(fav.image || process.env.PUBLIC_URL + "/news_icon2.jpg")}
                  alt="article"  
                />
              ))}
            </div>
            <div className="ml-4 mb-4">
              <label htmlFor="visible" className="text-white">
                Visibilité de la revue de presse :
              </label>
              <select id="visible" className="ml-2 p-1 rounded" value={visible} onChange={(e) => setVisible(parseInt(e.target.value))}>
                {VISIBILITY_OPTIONS.map((option) => (
                  <option value={option.value} key={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div className="button-container mr-4 mb-4 flex justify-end">
              <button 
                  className={`bg-white text-blue-700 font-bold py-2 px-5 rounded ${
                    isLoading || !canValidate ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  onClick={handleValidate}
                  disabled={isLoading || !canValidate}
                >
                  Valider
              </button>
            </div>
          </div>
        </div>
      )}

      {showFactCheckingPopup && (
        <GenerateMultiFactChecking 
          articleLinks={selection.map(item => helpers.htmlDecode(item.link))}
          setShowPopUpFactChecking={setShowFactCheckingPopup}
          addReview={addReview}
        />
      )}

      <section className="articlesfav">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Rechercher parmi les favoris..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="flex flex-col lg:flex-row justify-around lg:justify-center mt-4 lg:mt-14 mb-9 lg:space-x-6">
          <button 
            onClick={togglePopup} 
            className="mt-2 lg:-mt-12 border-none py-2.5 px-4 text-white bg-[#009AAD] text-sm rounded-full font-bold font-sans flex items-center justify-center w-auto h-auto"
            title="Créer une nouvelle publication"
          >
            Publier
          </button>
          
          <button 
            onClick={addToExistingReview} 
            disabled={!isOneSelected} 
            className={`mt-2 lg:-mt-12 border-none py-2.5 px-4 text-white bg-[#009AAD] text-sm rounded-full font-bold font-sans flex items-center justify-center w-auto h-auto ${
              !isOneSelected ? 'opacity-50' : ''
            }`}
            title="Ajouter un article à une publication existante"
          >
            Republier
          </button>
          
          <button 
            onClick={addToExistingFriendReview} 
            disabled={!isOneSelected} 
            className={`mt-2 lg:-mt-12 border-none py-2.5 px-4 text-white bg-[#009AAD] text-sm rounded-full font-bold font-sans flex items-center justify-center w-auto h-auto ${
              !isOneSelected ? 'opacity-50' : ''
            }`}
            title="Publier en collaboration avec un ami"
          >
            Co-publier
          </button>
          
          <button 
            onClick={handleRemoveMultipleFavorites} 
            disabled={!isOneSelected} 
            className={`mt-2 lg:-mt-12 border-none py-2.5 px-4 text-white bg-[#009AAD] text-sm rounded-full font-bold font-sans flex items-center justify-center w-auto h-auto ${
              !isOneSelected ? 'opacity-50' : ''
            }`}
          >
            Supprimer
          </button>
          
          <button 
            onClick={handleSelectAll} 
            className="mt-2 lg:-mt-12 border-none py-2.5 px-4 text-white bg-[#009AAD] text-sm rounded-full font-bold font-sans flex items-center justify-center w-auto h-auto"
          >
              {selection.length === filteredFavorites.length ? 'Tout décocher' : 'Tout cocher'}
          </button>
        </div>

        <div ref={listRef} className="allArticlesDiv h-100 overflow-y-auto" style={{ maxHeight: "calc(100vh - 250px)" }}>
          {filteredFavorites.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Aucun favori trouvé</p>
            </div>
          ) : (
            filteredFavorites.map((fav, i) => (
              <div className="featuresfav" key={fav.id || i}>
                <div className="itemfav rounded-lg shadow m-2">
                  <div className="item-photowrapper">
                    <img
                      className="item-photofav"
                      src={helpers.htmlDecode(fav.image || process.env.PUBLIC_URL + "/news_icon2.jpg")}
                      alt="article"
                      onError={(event) => {
                        event.currentTarget.onerror = null
                        event.currentTarget.src = process.env.PUBLIC_URL + "/news_icon2.jpg"
                      }}
                      onClick={() => window.open(helpers.htmlDecode(fav.link), "_blank")} 
                    />
                  </div>
                  <div className="arttfav">
                    <div className="item-headerfav"></div>
                    <a href={helpers.htmlDecode(fav.link)} target="_blank" rel="noreferrer" style={{ textDecoration: "inherit", color: "inherit" }}>
                      <h3 className="item-titlefav">
                        {helpers.htmlDecode(fav.title)}
                      </h3>
                      <p>
                        <strong className="text-cyan-600">
                          {helpers.getDomainName(fav.link) === "static.zemus.info" ? "Article - " : "Recherche - "}
                          Pays : {helpers.htmlDecode(fav.country)} | Source : {helpers.getDomainName(fav.link)} 
                        </strong>
                      </p>  
                      <p className="item-textfav">
                        {helpers.htmlDecode(fav.description)}
                      </p>
                    </a>
                    <div>
                      {renderReviews(fav.link, i)}
                    </div>
                  </div>

                  <div className="actiondiv">
                    <input 
                      type="checkbox" 
                      checked={selection.some(selected => selected.id === fav.id)}
                      onChange={(e) => handleSelectionChange(fav.id, e.target.checked)}
                    />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      <button 
        onClick={scrollToTop} 
        className="fixed bottom-6 right-6 bg-[#009AAD] text-white p-5 rounded-full shadow-lg hover:bg-[#007B8F] transition-colors"
      >
        ↑
      </button>


      <style jsx>{`
        .loading-spinner-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 1rem;
        }
        
        .loading-spinner {
          border: 4px solid rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          border-top: 4px solid #3B82F6;
          width: 2rem;
          height: 2rem;
          animation: spin 1s linear infinite;
          margin-bottom: 0.5rem;
        }
        
        .loading-text {
          color: white;
          font-size: 0.875rem;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .transform {
          transition-property: transform;
          transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
          transition-duration: 300ms;
        }
        
        .rotate-180 {
          transform: rotate(180deg);
        }
      `}</style>
    </>
  );
};

export default ArticlesFav;
